import styled from 'styled-components';
import getThemeByProps from '@userzoomlibraries/toolkit-theme-manager/get-theme-by-props';
import React, { useRef, useImperativeHandle } from 'react';
import PropTypes from '@userzoomlibraries/toolkit-js-utils/prop-types';
import LinkedFloatingElement from '@userzoomlibraries/toolkit-floating-element/linked-floating-element';
import isFunction from '@userzoomlibraries/toolkit-js-utils/is-function';
import useAriaAttributes from '@userzoomlibraries/toolkit-react-utils/use-aria-attributes';
import useDataAttributes from '@userzoomlibraries/toolkit-react-utils/use-data-attributes';
import useProps from '@userzoomlibraries/toolkit-react-utils/use-props';
import { C as Content } from './index.styled5.js';
import { C as CloseButton } from './index.styled9.js';
import { H as Header } from './index.styled6.js';
import { B as Body } from './index.styled7.js';
import { F as Footer } from './index.styled8.js';
import { T as Trigger } from './index.styled4.js';
import { P as PLACEMENTS } from './index.js';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["className", "children", "isOpened"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var RelativeDialog$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
      children = _ref.children,
      isOpened = _ref.isOpened,
      rest = _objectWithoutProperties(_ref, _excluded);

  var relativeDialogRef = useRef();
  var ariaAttributes = useAriaAttributes(rest);
  var dataAttributes = useDataAttributes(rest);
  var otherProps = useProps(rest, {
    excluded: ['aria-*', 'data-*']
  });
  var childrenToArray = React.Children.toArray(children);
  var triggerToRender = childrenToArray.find(function (child) {
    return child.type === Trigger;
  });
  var triggerChildrenToRender = isFunction(triggerToRender.props.children) ? triggerToRender.props.children : function () {
    return triggerToRender.props.children;
  };
  var contentToRender = childrenToArray.find(function (child) {
    return child.type === Content;
  });
  useImperativeHandle(ref, function () {
    return relativeDialogRef.current;
  });
  return /*#__PURE__*/jsx(LinkedFloatingElement, _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, otherProps), {}, {
    className: "gz-relative-dialog ".concat(className),
    ref: relativeDialogRef,
    trigger: "manual",
    content: contentToRender,
    opened: isOpened
  }, ariaAttributes), dataAttributes), {}, {
    children: triggerChildrenToRender()
  }));
});
RelativeDialog$1.PLACEMENTS = PLACEMENTS;
RelativeDialog$1.Content = Content;
RelativeDialog$1.CloseButton = CloseButton;
RelativeDialog$1.Header = Header;
RelativeDialog$1.Body = Body;
RelativeDialog$1.Footer = Footer;
RelativeDialog$1.Trigger = Trigger;
RelativeDialog$1.COMPONENT_ID = 'Gearz.RelativeDialog';
RelativeDialog$1.displayName = RelativeDialog$1.COMPONENT_ID;
var relativeDialogPropTypes = Object.keys(LinkedFloatingElement.propTypes).filter(function (each) {
  return !['trigger', 'content', 'opened', 'placement'].includes(each);
}).reduce(function (acc, current) {
  return _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, current, LinkedFloatingElement.propTypes[current]));
}, {});
var relativeDialogDefaultProps = Object.keys(LinkedFloatingElement.defaultProps).filter(function (each) {
  return !['trigger', 'content', 'opened', 'placement'].includes(each);
}).reduce(function (acc, current) {
  return _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, current, LinkedFloatingElement.defaultProps[current]));
}, {});
RelativeDialog$1.propTypes = _objectSpread(_objectSpread({}, relativeDialogPropTypes), {}, {
  placement: PropTypes.oneOfWithDescription(Object.values(PLACEMENTS), 'Placement of the relative dialog.'),
  isOpened: PropTypes.boolWithDescription('Flag to determine if the relative dialog is opened.'),
  children: PropTypes.nodeWithDescription('Content of the relative dialog.')
});
RelativeDialog$1.defaultProps = _objectSpread(_objectSpread({}, relativeDialogDefaultProps), {}, {
  placement: PLACEMENTS.BOTTOM,
  isOpened: false,
  children: null
});

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var RelativeDialog = styled(RelativeDialog$1).withConfig({
  componentId: "gz-dialog-1674727614741__sc-o0b8yw-0"
})(["box-sizing:border-box;", ""], function (props) {
  return getThemeByProps({
    componentId: RelativeDialog$1.COMPONENT_ID,
    props: props
  });
});
RelativeDialog.displayName = RelativeDialog$1.displayName;
RelativeDialog.propTypes = RelativeDialog$1.propTypes;
RelativeDialog.defaultProps = RelativeDialog$1.defaultProps;

export { RelativeDialog as R };
